import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { useReservation } from '../../api/getReservation';
import { useRestaurant } from '../../api/getRestaurant';
import { useModifyReservation } from '../../api/modifyReservation';
import { URLParams } from '../../common/types';
import { SquareIconButton } from '../../components/buttons/SquareIconButton';
import SubmitButton from '../../components/buttons/SubmitButton';
import { Header } from '../../components/checkout/Header';
import { DiscardChanges } from '../../components/dialog/controlled-dialog/DiscardChanges';
import { RHFPhoneInput } from '../../components/form-inputs/RHFPhoneInput';
import { RHFTextInput } from '../../components/form-inputs/RHFTextInput';
import { countryCodeConverter } from '../../helpers/countryCodeConverter';
import { trackReservationModifications } from '../../helpers/reservationEventTrackers';
import layoutStyles from '../../layouts/ModificationLayout.module.scss';
import { useTranslation } from 'react-i18next';

export function GuestDetailsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { reservationId } = useParams<URLParams>();

	const { data: reservation } = useReservation(reservationId);
	const { data: restaurant } = useRestaurant(reservation.restaurantId);
	const modifyReservationMutation = useModifyReservation(reservationId);

	const [isConfirmOpen, setIsConfirmOpen] = useState(false);

	const NAME_REGEX = new RegExp(/^[^±!@£$%^&*_+§¡€#¢§¶•ªº«\\/<>?:;|=.,]{1,20}$/);

	// Schema & Types - Contact Info
	const ContactFormSchema = z.object({
		firstName: z
			.string({ required_error: t('errors.first_name_required') })
			.min(2, t('errors.first_name_min_length'))
			.max(20, t('errors.first_name_max_length'))
			.refine(
				(value) => {
					// Using the following syntax to avoid issues with names not using english alphabet, (e.g. Chinese, Japanese, Eastern EU etc.)
					return NAME_REGEX.test(value);
				},
				{ message: t('errors.name_special_characters') }
			),
		fullName: z.string(),
		lastName: z
			.string({ required_error: t('errors.last_name_required') })
			.min(2, t('errors.last_name_min_length'))
			.max(20, t('errors.last_name_max_length'))
			.refine(
				(value) => {
					// Using the following syntax to avoid issues with names not using english alphabet, (e.g. Chinese, Japanese, Eastern EU etc.)
					return NAME_REGEX.test(value);
				},
				{ message: t('errors.name_special_characters') }
			),
		email: z.string({ required_error: t('errors.email_required') }).email(t('errors.invalid_email')).transform((value) => value?.trim()).refine(
			(value) => {
				//Consider most common email typo as a validation error
				return !!value && !value.endsWith('@gmail.con');
			},
			{ message: t('errors.email_domain_invalid') }
		),
		phone: z.string({ required_error: t('errors.phone_required') }).refine((val) => isValidPhoneNumber(val), {
			message: t('errors.invalid_phone_number')
		})
	});

	type ContactFormType = z.infer<typeof ContactFormSchema>;

	const defaultValues = {
		firstName: '',
		lastName: '',
		fullName: '',
		email: '',
		phone: ''
	};
	// Form Logic & State
	const {
		reset,
		register,
		control,
		formState: { errors, isValid, isDirty },
		handleSubmit,
		setValue,
		getValues
	} = useForm<ContactFormType>({
		mode: 'onTouched',
		resolver: zodResolver(ContactFormSchema),
		defaultValues,
		values: reservation
	});

	const resetFormAndExit = () => {
		reset();
		navigate(`/booking/${reservationId}`);
	};

	const handleExitClick = () => {
		if (isDirty) {
			setIsConfirmOpen(true);
		}
		else {
			resetFormAndExit();
		}
	};

	const handleConfirmExit = () => {
		resetFormAndExit();
		setIsConfirmOpen(false);
	};

	const handleCancelExit = () => {
		setIsConfirmOpen(false);
	};

	const handleFormSubmit = async (data: ContactFormType) => {
		// TODO Full name not updated
		const response = await modifyReservationMutation.mutateAsync({
			...data,
			restaurantId: reservation.restaurantId,
			id: reservation.id
		});
		if (response.success) {
			navigate(`/booking/${reservationId}`);
			trackReservationModifications({ reservation, itemsChanged: { ...data } });
			toast.success(t('messages.changes_saved', { defaultValue: 'Changes saved successfully!' }));
		}
	};

	return (
		<>
			<div className={layoutStyles.Header}>
				<Header
					title={t('titles.contact_details')}
					subtitle={t('titles.modify_booking')}
					iconLeft={<></>}
					iconRight={<SquareIconButton variant='exit' />}
					iconRightOnClick={handleExitClick}
				/>
			</div>

			<main className={layoutStyles.Body}>
				<form
					id='contact-details-form'
					className={`${layoutStyles.Section} ${layoutStyles['Section--transparent']}`}
					onSubmit={handleSubmit(handleFormSubmit)}
				>
					<RHFTextInput
						label={t('forms.first_name', { defaultValue: 'First Name' })}
						name='firstName'
						register={register}
						error={errors.firstName?.message}
						onChange={(evt) => {
							const lastName = getValues(['lastName']);
							setValue('fullName', `${evt.target.value} ${lastName}`, {
								shouldDirty: true
							});
						}}
					/>
					<RHFTextInput
						label={t('forms.last_name', { defaultValue: 'Last Name' })}
						name='lastName'
						register={register}
						error={errors.lastName?.message}
						onChange={(evt) => {
							const firstName = getValues(['firstName']);
							setValue('fullName', `${firstName} ${evt.target.value}`, {
								shouldDirty: true
							});
						}}
					/>
					<RHFTextInput label={t('forms.email', { defaultValue: 'Email' })} name='email' register={register} error={errors.email?.message} />
					<RHFPhoneInput
						label={t('forms.phone_number', { defaultValue: 'Phone Number' })}
						name='phone'
						control={control}
						defaultCountry={countryCodeConverter(restaurant.country ?? restaurant.stripeRegion)}
						error={errors.phone?.message}
					/>
				</form>
			</main>

			<footer className={layoutStyles.Footer}>
				<SubmitButton
					form='contact-details-form'
					variant='transparent'
					buttonText={t('actions.save_changes')}
					disabled={!isDirty || modifyReservationMutation.isPending || (isDirty && !isValid)}
					loading={modifyReservationMutation.isPending}
				/>
			</footer>

			<DiscardChanges
				open={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onDiscard={handleConfirmExit}
				onCancel={handleCancelExit}
			/>
		</>
	);
}